import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface SellerAnalysisConfig {
  acosMax: number;
  tacosMax: number;
  ctrMin: number | null;
  cvrMin: number | null;
  ratingMin: number;
  cooldownDays: number;
  analysesUntilCut: number;
  imageAnalysisEnabled: boolean;
  autoAnalysis: boolean;
  autoAnalysisIntervalHours: number;
}

export interface AnalysisMetrics {
  impressions: number;
  clicks: number;
  visitors: number;
  orders: number;
  revenue: number;
  addToCart: number;
  units: number;
  ctr: number;
  cvr: number;
  atcRate: number;
  adSpend: number;
  adAcos: number;
  adRoas: number;
  adRevenue: number;
  tacos: number;
  paidCost: number;
  affiliateCommission: number;
  affiliateSales: number;
  ratingAverage: number | null;
  reviewsCount: number | null;
  photoCount: number;
  videoCount: number;
  hasPromotion: boolean;
  discountPercent: number;
  currentPrice: number | null;
  benchmark: { ctrMin: number; ctrStrong: number; cvrMin: number; usedAccountMedian: boolean };
}

export interface AnalysisDiagnosis {
  headline: string;
  evidencias: string[];
  hipoteses: string[];
  verificarManualmente: string[];
  acoes: string[];
  testeSugerido: { mudanca: string; metricaAlvo: string; janela: string } | null;
  reavaliarEm: string;
  motivoDoMotor: string;
  source?: 'llm' | 'fallback' | 'deterministic';
  llmError?: string | null;
}

export interface AnalysisVisual {
  tipo: string;
  produtoPercentualFrame: string;
  scores: Record<string, number>;
  classificacao: string;
  melhorias: string[];
  pontosPositivos?: string[];
}

export interface HealthCard {
  id: string;
  listingIdExt: string;
  connectionId: string | null;
  marketplace: string;
  periodDays: number;
  title: string | null;
  sku: string | null;
  listingUrl: string | null;
  imageUrl: string | null;
  state: string;
  stateLabel: string;
  column: string;
  urgency: string;
  confidence: string;
  analysisNumber: number;
  reviewStatus: string;
  cooldownUntil: string | null;
  analyzedAt: string;
  headline: string | null;
  keyEvidence: string[];
  metrics: AnalysisMetrics;
  hasVisual: boolean;
}

export interface AnalysisRecord extends HealthCard {
  previousAnalysisId: string | null;
  diagnosis: AnalysisDiagnosis;
  visual: AnalysisVisual | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export interface RunProgress {
  status: 'running' | 'completed' | 'failed';
  total: number;
  done: number;
  analyzed: number;
  skipped: number;
  disabled?: number;
  periodDays: number;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
}

type BoardColumns = Record<'critico' | 'problema' | 'atencao' | 'saudavel' | 'sem_dados' | 'revisados', HealthCard[]>;

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function useSellerAnalysis(filters: { marketplace?: string; connectionId?: string } = {}) {
  const { user } = useAuth();
  const [columns, setColumns] = useState<BoardColumns | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [running, setRunning] = useState(false);

  const fetchBoard = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.marketplace) params.set('marketplace', filters.marketplace);
      if (filters.connectionId) params.set('connectionId', filters.connectionId);
      const res = await fetch(`/api/seller/analysis/board?${params.toString()}`, { headers: getHeaders() });
      const json = await res.json();
      if (json?.success) {
        setColumns(json.columns);
        setTotal(json.total || 0);
      }
    } catch {
      // silencioso: o board simplesmente fica vazio
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, filters.marketplace, filters.connectionId]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  // Heartbeat de progresso: detecta QUALQUER analise ativa (manual, automatica ou
  // iniciada em outra tela) e transmite o board conforme os anuncios sao analisados.
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (!user?.tenantId) return;
    let stopped = false;
    let timer: number;

    const tick = async () => {
      try {
        const res = await fetch('/api/seller/analysis/run/status', { headers: getHeaders() });
        const json = await res.json();
        const p: RunProgress | null = json?.progress || null;
        if (stopped) return;
        setProgress(p);
        const isRunning = p?.status === 'running';
        setRunning(isRunning);
        // Enquanto roda: puxa o board a cada tick (cards aparecem em tempo real).
        // Ao terminar: um refetch final para consistencia.
        if (isRunning || wasRunningRef.current) fetchBoard();
        wasRunningRef.current = isRunning;
      } catch {
        // ignora falhas pontuais de rede
      }
      if (stopped) return;
      // Rapido enquanto roda (2.5s); devagar quando ocioso (6s), para detectar novas rodadas.
      timer = window.setTimeout(tick, wasRunningRef.current ? 2500 : 6000);
    };

    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [user?.tenantId, fetchBoard]);

  const runAnalysis = useCallback(async (opts: { periodDays?: number; marketplace?: string; connectionId?: string } = {}) => {
    const res = await fetch('/api/seller/analysis/run', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(opts),
    });
    const json = await res.json();
    if (json?.success) {
      setProgress(json.progress);
      setRunning(true);
    }
    return json;
  }, []);

  const fetchHistory = useCallback(async (listingIdExt: string): Promise<AnalysisRecord[]> => {
    const res = await fetch(`/api/seller/analysis/${encodeURIComponent(listingIdExt)}/history`, { headers: getHeaders() });
    const json = await res.json();
    return json?.success ? json.history : [];
  }, []);

  const reviewAnalysis = useCallback(async (id: string, patch: { reviewStatus?: string; reviewNotes?: string; urgency?: string }) => {
    const res = await fetch(`/api/seller/analysis/${id}/review`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(patch),
    });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  const deleteHistory = useCallback(async (listingIdExt: string) => {
    const res = await fetch(`/api/seller/analysis/${encodeURIComponent(listingIdExt)}/history`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  const deleteAllHistory = useCallback(async () => {
    const res = await fetch('/api/seller/analysis/history', {
      method: 'DELETE',
      headers: getHeaders(),
    });
    const json = await res.json();
    if (json?.success) fetchBoard();
    return json;
  }, [fetchBoard]);

  return { columns, total, loading, progress, running, runAnalysis, fetchBoard, fetchHistory, reviewAnalysis, deleteHistory, deleteAllHistory };
}

// Preferencia de analise por anuncio (ON/OFF). Sem entrada = ON por padrao.
export function useAnalysisPrefs() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});

  const fetchPrefs = useCallback(async () => {
    if (!user?.tenantId) return;
    try {
      const res = await fetch('/api/seller/analysis/prefs', { headers: getHeaders() });
      const json = await res.json();
      if (json?.success) {
        const map: Record<string, boolean> = {};
        for (const p of json.prefs) map[p.listingIdExt] = p.enabled;
        setPrefs(map);
      }
    } catch {
      // ignora
    }
  }, [user?.tenantId]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const isEnabled = useCallback((listingIdExt: string) => prefs[listingIdExt] !== false, [prefs]);

  const setEnabled = useCallback(async (ids: string | string[], enabled: boolean) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    // otimista
    setPrefs((prev) => {
      const next = { ...prev };
      for (const id of arr) next[id] = enabled;
      return next;
    });
    await fetch('/api/seller/analysis/prefs', {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ listingIdExts: arr, enabled }),
    }).catch(() => {});
  }, []);

  return { prefs, isEnabled, setEnabled, refetch: fetchPrefs };
}

export function useSellerAnalysisConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState<SellerAnalysisConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const res = await fetch('/api/seller/analysis/config', { headers: getHeaders() });
      const json = await res.json();
      if (json?.success) setConfig(json.config);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = useCallback(async (patch: Partial<SellerAnalysisConfig>) => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/analysis/config', {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (json?.success) setConfig(json.config);
      return json;
    } finally {
      setSaving(false);
    }
  }, []);

  return { config, loading, saving, saveConfig, refetch: fetchConfig };
}
